import { useEffect, useState } from "react";
import { api, formatApiError, notifyMutation } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Buildings } from "@phosphor-icons/react";
import { DeleteStockGuard, EditStockConfirm, fetchStockSummary } from "@/components/StockGuard";

export default function ManufacturersPage() {
  const { isStaff } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [catalogueUrl, setCatalogueUrl] = useState("");
  const [editStock, setEditStock] = useState(null);
  const [confirmEditOpen, setConfirmEditOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/manufacturers");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setEditStock(null); setName(""); setDescription(""); setCatalogueUrl(""); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setEditStock(null); setName(m.name); setDescription(m.description || ""); setCatalogueUrl(m.catalogue_url || ""); setOpen(true); };

  const doSave = async () => {
    setConfirmEditOpen(false);
    try {
      let res;
      if (editing) res = await api.put(`/manufacturers/${editing.id}`, { name, description, catalogue_url: catalogueUrl });
      else res = await api.post("/manufacturers", { name, description, catalogue_url: catalogueUrl });
      notifyMutation(res, editing ? "Manufacturer updated" : "Manufacturer created");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const handleSave = async () => {
    if (!editing) { doSave(); return; }
    // Always re-validate fresh at save time (never trust cached/stale stock state).
    const stock = await fetchStockSummary("manufacturer", editing.id);
    if (stock.error) { toast.error("Couldn't verify stock — please try again."); return; }
    setEditStock(stock);
    if (stock.has_stock) { setConfirmEditOpen(true); return; }
    doSave();
  };

  const onDelete = async (m) => {
    try { const res = await api.delete(`/manufacturers/${m.id}`); notifyMutation(res, "Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="p-8 max-w-[1200px]">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Catalogue</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">Manufacturers</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="add-manufacturer-btn">
              <Plus size={16} /> Add Manufacturer
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Manufacturer</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="3M, Hexis, Teckwrap…" data-testid="mfr-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Description</Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Catalogue URL (optional)</Label>
                <Input value={catalogueUrl} onChange={(e) => setCatalogueUrl(e.target.value)} placeholder="https://brand.com/colour-range" data-testid="mfr-catalogue-url-input" />
                <p className="text-[11px] text-slate-500">Optional. Used by Product Radar — a direct link to this brand's colour range helps the AI scan be more accurate. Leave blank to search the web by brand name.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
              <Button onClick={handleSave} disabled={!name} className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="mfr-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      {items.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-16 text-center">
          <Buildings size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500">No manufacturers yet. Click <strong>Add Manufacturer</strong> to start.</div>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Description</th>
                <th className="px-6 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-6 py-3 font-medium">{m.name}</td>
                  <td className="px-6 py-3 text-slate-600">{m.description || "—"}</td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-mfr-${m.id}`}>
                        <PencilSimple size={14} />
                      </Button>
                      <DeleteStockGuard entityType="manufacturer" id={m.id} name={m.name} isStaff={isStaff} onConfirm={() => onDelete(m)} testId={`delete-mfr-${m.id}`} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EditStockConfirm open={confirmEditOpen} onOpenChange={setConfirmEditOpen} entityType="manufacturer" name={editing?.name} summary={editStock?.summary} onConfirm={doSave} />
    </div>
  );
}
